import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/AuthContext';
import { UserCircleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { AuthDialog } from './AuthDialog';

/**
 * Sign in / Sign out controls for the navigation bar.
 * Renders nothing when Convex Auth is not configured.
 * When unauthenticated: a "Sign in" button that opens the AuthDialog modal.
 * When authenticated: a "Sign out" button.
 */
export const AuthNavButtons: React.FC = () => {
  const auth = useAuthContext();
  const { t } = useTranslation();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  if (auth == null) {
    return null;
  }

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await auth.signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  if (auth.isLoading) {
    return (
      <span className="text-sm text-slate-500 dark:text-slate-400">
        {t('auth.loading', 'Loading…')}
      </span>
    );
  }

  if (auth.isAuthenticated) {
    return (
      <button
        type="button"
        onClick={handleSignOut}
        disabled={isSigningOut}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-surface-800"
        title={t('auth.signOut', 'Sign out')}
        aria-label={t('auth.signOut', 'Sign out')}
      >
        <ArrowRightOnRectangleIcon className="h-5 w-5" />
        <span className="hidden sm:inline">{t('auth.signOut', 'Sign out')}</span>
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsAuthDialogOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-surface-800"
        title={t('auth.signIn', 'Sign in')}
        aria-label={t('auth.signIn', 'Sign in')}
      >
        <UserCircleIcon className="h-5 w-5" />
        <span className="hidden sm:inline">{t('auth.signIn', 'Sign in')}</span>
      </button>
      <AuthDialog isOpen={isAuthDialogOpen} onClose={() => setIsAuthDialogOpen(false)} />
    </>
  );
};
